/**
 * Deposit Verification Modal - SOP #3
 * Handles deposit verification with receipt generation and template display.
 * Admins can override the required deposit (incl. R0 waiver) before verifying.
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  Form,
  Input,
  Button,
  Space,
  message,
  Card,
  Typography,
  Tabs,
  InputNumber,
  Alert,
  Upload,
  Tag,
  Image,
} from "antd";
import {
  CheckCircleOutlined,
  CopyOutlined,
  CheckOutlined,
  DollarOutlined,
  MessageOutlined,
  MailOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileImageOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  verifyDeposit,
  getDepositReceipt,
  uploadDepositPop,
} from "../services/depositService";
import { copyToClipboard } from "../../leads/services/communicationTemplateService";
import { logError } from "../../../shared/utils/logger.js";
import {
  usePublicSettings,
  getPublicSetting,
} from "../../../hooks/usePublicSettings.js";
import { getQuoteTotal } from "../services/quoteOperationalStatus.js";
import api from "../../../shared/api";
import ResponsiveDrawer from "../../../shared/components/ResponsiveDrawer.jsx";
import styled from "styled-components";

const { TextArea } = Input;
const { Text } = Typography;
const { TabPane } = Tabs;

const PopFilesRow = styled.div`
  margin-top: 12px;
`;

const PopSuggestionsBlock = styled.div`
  margin-top: 12px;
`;

const SuggestionField = styled.div`
  margin-bottom: 8px;
`;

const DEFAULT_DEPOSIT_PERCENT = 20;

/**
 * Resolve the quote's current required deposit (override-aware).
 * @param {Object|null|undefined} quote
 * @param {number} totalCost
 * @param {number} configuredMinPercent
 * @returns {number}
 */
export function resolveExpectedDeposit(
  quote,
  totalCost,
  configuredMinPercent
) {
  if (quote?.payment?.requiredAmountOverridden === true) {
    const overridden = Number(quote.payment.requiredAmount);
    if (Number.isFinite(overridden) && overridden >= 0) {
      return overridden;
    }
  }
  const fromApi = Number(quote?.deposit?.requiredAmount);
  if (Number.isFinite(fromApi) && fromApi >= 0) {
    return fromApi;
  }
  const fromSop = Number(quote?.sop2Messaging?.depositAmount);
  if (Number.isFinite(fromSop) && fromSop >= 0) {
    return fromSop;
  }
  if (!Number.isFinite(totalCost) || totalCost <= 0) {
    return 0;
  }
  return Math.round(totalCost * (configuredMinPercent / 100));
}

const DepositVerificationModal = ({
  visible,
  quote,
  onClose,
  onSuccess,
  onQuoteRefresh,
  userId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [depositVerified, setDepositVerified] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [whatsappCopied, setWhatsappCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [whatsappTemplate, setWhatsappTemplate] = useState("");
  const [emailTemplate, setEmailTemplate] = useState({ subject: "", body: "" });
  const [receiptPdfUrl, setReceiptPdfUrl] = useState(null);
  const [popUploading, setPopUploading] = useState(false);
  const [popFiles, setPopFiles] = useState([]);
  const [popSuggestions, setPopSuggestions] = useState(null);
  const [acceptedSuggestions, setAcceptedSuggestions] = useState({
    amount: false,
    reference: false,
  });

  const [requiredDeposit, setRequiredDeposit] = useState(0);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideTouched, setOverrideTouched] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);

  const { settings: publicSettings } = usePublicSettings();
  const minDepositPercent = Number(
    getPublicSetting(
      publicSettings,
      "payments.deposit.minPercent",
      DEFAULT_DEPOSIT_PERCENT
    )
  );
  const configuredMinPercent = Number.isFinite(minDepositPercent)
    ? minDepositPercent
    : DEFAULT_DEPOSIT_PERCENT;

  const totalCost = useMemo(() => getQuoteTotal(quote), [quote]);
  const defaultDeposit = useMemo(() => {
    if (!Number.isFinite(totalCost) || totalCost <= 0) return 0;
    return Math.round(totalCost * (configuredMinPercent / 100));
  }, [totalCost, configuredMinPercent]);

  const baselineRequired = useMemo(
    () => resolveExpectedDeposit(quote, totalCost, configuredMinPercent),
    [quote, totalCost, configuredMinPercent]
  );

  const hasExistingOverride =
    quote?.payment?.requiredAmountOverridden === true;

  // Sync editable required deposit when the modal opens / quote changes
  useEffect(() => {
    if (!visible || !quote) return;
    setRequiredDeposit(baselineRequired);
    setOverrideReason("");
    setOverrideTouched(false);
    setResetRequested(false);
    form.setFieldsValue({
      depositAmount: baselineRequired,
      paymentReference: undefined,
      verificationNotes: undefined,
    });
  }, [visible, quote?.id, baselineRequired, form, quote]);

  // Prefill POP state when the modal opens (not on every parent refresh while open)
  useEffect(() => {
    if (!visible || !quote) return;
    const existing =
      (Array.isArray(quote.payment?.adminPops) && quote.payment.adminPops) ||
      quote.payment?.proofData?.proofFiles?.filter(
        (f) => f?.source === "admin_upload"
      ) ||
      quote.payment?.proofs
        ?.filter((p) => p?.via === "admin")
        ?.flatMap((p) => p.files || []) ||
      [];
    setPopFiles(existing);
    if (quote.payment?.popExtraction) {
      setPopSuggestions(quote.payment.popExtraction);
    } else if (!existing.length) {
      setPopSuggestions(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate only on open / quote id change
  }, [visible, quote?.id]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(amount);
  };

  const applyRequiredDeposit = (nextAmount, { touched = true, reset = false } = {}) => {
    setRequiredDeposit(nextAmount);
    setOverrideTouched(touched);
    setResetRequested(reset);
    if (reset) {
      setOverrideReason("");
    }
    form.setFieldsValue({ depositAmount: nextAmount });
  };

  const handleResetToDefault = () => {
    applyRequiredDeposit(defaultDeposit, { touched: false, reset: true });
  };

  const handlePopUpload = async (file) => {
    if (!quote?.id) {
      message.error("Quote not loaded");
      return false;
    }
    setPopUploading(true);
    try {
      const result = await uploadDepositPop(quote.id, [file], {
        adminId: userId,
      });
      if (!result.success) {
        message.error(result.error || "Failed to upload POP");
        return false;
      }
      const files = result.data?.files || [];
      setPopFiles((prev) => [...prev, ...files]);
      const suggestions = result.data?.suggestions || null;
      setPopSuggestions(suggestions);
      setAcceptedSuggestions({ amount: false, reference: false });
      if (typeof onQuoteRefresh === "function") {
        onQuoteRefresh({
          files,
          suggestions,
          quoteId: quote.id,
        });
      }
      message.success("POP uploaded. Review suggested fields below.");
    } catch (error) {
      logError("POP upload failed", { error: error.message });
      message.error("Failed to upload POP");
    } finally {
      setPopUploading(false);
    }
    return false; // prevent antd auto-upload
  };

  const acceptSuggestion = (field) => {
    if (!popSuggestions) return;
    if (field === "amount" && popSuggestions.amount != null) {
      form.setFieldsValue({ depositAmount: popSuggestions.amount });
      setAcceptedSuggestions((s) => ({ ...s, amount: true }));
      message.success("Suggested amount applied");
    }
    if (field === "reference" && popSuggestions.reference) {
      form.setFieldsValue({ paymentReference: popSuggestions.reference });
      setAcceptedSuggestions((s) => ({ ...s, reference: true }));
      message.success("Suggested reference applied");
    }
  };

  const rejectSuggestion = (field) => {
    setAcceptedSuggestions((s) => ({ ...s, [field]: false }));
    message.info(
      field === "amount"
        ? "Amount suggestion ignored — enter manually"
        : "Reference suggestion ignored — enter manually"
    );
  };

  const persistOverrideIfNeeded = async () => {
    if (!overrideTouched && !resetRequested) {
      return { ok: true, required: requiredDeposit };
    }

    if (
      overrideTouched &&
      !resetRequested &&
      String(overrideReason || "").trim().length < 3
    ) {
      message.error("Enter a reason for the deposit override (min 3 characters).");
      return { ok: false };
    }

    if (!Number.isFinite(requiredDeposit) || requiredDeposit < 0) {
      message.error("Deposit amount is invalid for this quote.");
      return { ok: false };
    }

    const hasUsableTotal = Number.isFinite(totalCost) && totalCost > 0;
    // Backend caps override to quote total; a missing/zero total cannot accept a non-zero override.
    if (
      !hasUsableTotal &&
      overrideTouched &&
      !resetRequested &&
      requiredDeposit > 0
    ) {
      message.error(
        "This quote has no total amount. Set the quote price before overriding a non-zero deposit."
      );
      return { ok: false };
    }

    if (hasUsableTotal && requiredDeposit > totalCost) {
      message.error(
        `Deposit must be between R0 and ${formatCurrency(totalCost)}.`
      );
      return { ok: false };
    }

    const contentUpdates = resetRequested
      ? { depositOverrideAmount: null }
      : {
          depositOverrideAmount: requiredDeposit,
          depositOverrideReason: String(overrideReason).trim(),
        };

    const updateResponse = await api.patch(
      `/quotes/update/${quote.id}/content`,
      {
        contentUpdates,
        reason: resetRequested
          ? "Reset deposit override at verification (SOP #3)"
          : "Deposit override at verification (SOP #3)",
      }
    );

    if (updateResponse?.success === false) {
      throw new Error(
        updateResponse.error || "Failed to update deposit requirement"
      );
    }

    return {
      ok: true,
      required: resetRequested ? defaultDeposit : requiredDeposit,
    };
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      let persist;
      try {
        persist = await persistOverrideIfNeeded();
      } catch (error) {
        logError("Error updating deposit override before verify", {
          quoteId: quote?.id,
          error: error.message,
          stack: error.stack,
        });
        message.error(
          error.message || "Failed to update deposit requirement"
        );
        return;
      }
      if (!persist.ok) return;

      const expectedDeposit = persist.required;
      const isWaiver =
        expectedDeposit === 0 && Number(values.depositAmount) === 0;
      if (
        !isWaiver &&
        (!values.paymentReference ||
          values.paymentReference.trim().length < 3)
      ) {
        message.error("Payment reference is required (minimum 3 characters)");
        return;
      }

      if (
        values.depositAmount === undefined ||
        values.depositAmount === null ||
        values.depositAmount < 0
      ) {
        message.error("Deposit amount is required and cannot be negative");
        return;
      }

      const minAmount = expectedDeposit;
      const hasUsableTotal = Number.isFinite(totalCost) && totalCost > 0;
      const maxAmount = hasUsableTotal ? totalCost : Infinity;

      if (values.depositAmount < minAmount) {
        message.error(
          `Deposit amount must be at least the required ${formatCurrency(expectedDeposit)}. Received: ${formatCurrency(values.depositAmount)}`
        );
        return;
      }

      if (hasUsableTotal && values.depositAmount > maxAmount) {
        message.error(
          `Deposit amount cannot exceed the total amount of ${formatCurrency(totalCost)}. Received: ${formatCurrency(values.depositAmount)}`
        );
        return;
      }

      if (!hasUsableTotal && Number(values.depositAmount) > 0) {
        message.error(
          "This quote has no total amount. Set the quote price before verifying a non-zero deposit."
        );
        return;
      }

      const result = await verifyDeposit(quote.id, {
        adminId: userId,
        paymentReference: values.paymentReference?.trim() || "",
        depositAmount: values.depositAmount,
        verificationNotes: values.verificationNotes || "",
      });

      if (result.success) {
        setVerificationData(result.data);
        setDepositVerified(true);

        const templates = result.data.templates;
        setWhatsappTemplate(templates.whatsapp);
        setEmailTemplate(templates.email);

        if (result.data.receipt?.pdfBase64) {
          const pdfBlob = new Blob(
            [
              Uint8Array.from(atob(result.data.receipt.pdfBase64), (c) =>
                c.charCodeAt(0)
              ),
            ],
            { type: "application/pdf" }
          );
          const url = URL.createObjectURL(pdfBlob);
          setReceiptPdfUrl(url);
        }

        message.success("Deposit verified successfully!");
      } else {
        message.error(result.error || "Failed to verify deposit");
      }
    } catch (error) {
      logError("Error verifying deposit", {
        quoteId: quote.id,
        error: error.message,
        stack: error.stack,
      });
      message.error("Failed to verify deposit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!quote?.id) return;

    try {
      const blob = await getDepositReceipt(quote.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename =
        verificationData?.receipt?.filename ||
        `Receipt-${verificationData?.order?.id || quote.id}.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("Receipt downloaded successfully!");
    } catch (error) {
      logError("Error downloading receipt", {
        quoteId: quote.id,
        error: error.message,
        stack: error.stack,
      });
      const errorMessage = error.message || "Failed to download receipt";
      message.error(errorMessage);
    }
  };

  const handleCopyWhatsApp = async () => {
    if (!whatsappTemplate) return;

    const success = await copyToClipboard(whatsappTemplate);
    if (success) {
      setWhatsappCopied(true);
      message.success("WhatsApp template copied to clipboard!");
      setTimeout(() => setWhatsappCopied(false), 2000);
    } else {
      message.error("Failed to copy to clipboard");
    }
  };

  const handleCopyEmail = async () => {
    if (!emailTemplate.body) return;

    const emailText = `Subject: ${emailTemplate.subject}\n\n${emailTemplate.body}`;
    const success = await copyToClipboard(emailText);
    if (success) {
      setEmailCopied(true);
      message.success("Email template copied to clipboard!");
      setTimeout(() => setEmailCopied(false), 2000);
    } else {
      message.error("Failed to copy to clipboard");
    }
  };

  const handleClose = () => {
    if (depositVerified && onSuccess) {
      onSuccess(verificationData);
    }
    form.resetFields();
    setDepositVerified(false);
    setVerificationData(null);
    setWhatsappTemplate("");
    setEmailTemplate({ subject: "", body: "" });
    setWhatsappCopied(false);
    setEmailCopied(false);
    setOverrideReason("");
    setOverrideTouched(false);
    setResetRequested(false);
    setPopFiles([]);
    setPopSuggestions(null);
    setAcceptedSuggestions({ amount: false, reference: false });
    if (receiptPdfUrl) {
      if (typeof URL.revokeObjectURL === "function") {
        URL.revokeObjectURL(receiptPdfUrl);
      }
      setReceiptPdfUrl(null);
    }
    onClose();
  };

  const showOverrideReason =
    overrideTouched && !resetRequested;
  const showResetButton =
    hasExistingOverride ||
    overrideTouched ||
    (Number.isFinite(requiredDeposit) &&
      requiredDeposit !== defaultDeposit &&
      !resetRequested);

  const drawerTitle = depositVerified ? (
    <Space>
      <CheckCircleOutlined style={{ color: "#52c41a" }} />
      <span>Deposit Verified Successfully!</span>
    </Space>
  ) : (
    <Space>
      <DollarOutlined />
      <span>Verify Deposit (SOP #3)</span>
    </Space>
  );

  const drawerFooter = depositVerified ? (
    <div style={{ textAlign: "right" }}>
      <Button type="primary" onClick={handleClose} data-testid="deposit-drawer-close">
        Close
      </Button>
    </div>
  ) : (
    <div style={{ textAlign: "right" }}>
      <Space>
        <Button onClick={handleClose} disabled={loading} data-testid="deposit-drawer-cancel">
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          form="deposit-verification-form"
          loading={loading}
          icon={<CheckCircleOutlined />}
          data-testid="deposit-drawer-submit"
        >
          {requiredDeposit === 0 ? "Confirm Deposit Waiver" : "Verify Deposit"}
        </Button>
      </Space>
    </div>
  );

  return (
    <ResponsiveDrawer
      title={drawerTitle}
      open={visible}
      onClose={handleClose}
      footer={drawerFooter}
      desktopWidth={720}
      destroyOnHidden
      maskClosable={!loading}
    >
      {!depositVerified ? (
        <Form
          id="deposit-verification-form"
          key={`deposit-form-${quote?.id}-${baselineRequired}`}
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            depositAmount: baselineRequired,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Text strong>Quote: </Text>
            <Text>{quote.id}</Text>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Total Amount: </Text>
            <Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
              {Number.isFinite(totalCost) && totalCost > 0
                ? formatCurrency(totalCost)
                : "N/A"}
            </Text>
          </div>

          {!(Number.isFinite(totalCost) && totalCost > 0) && (
            <Alert
              message="Quote total is missing"
              description="Set the quote price/total before overriding or verifying a non-zero deposit. R0 waiver can still be confirmed."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Alert
            message={
              requiredDeposit === 0
                ? "Deposit waived"
                : `Required Deposit: ${formatCurrency(requiredDeposit)}`
            }
            description={
              requiredDeposit === 0
                ? "No funds or payment reference are required. Confirm the waiver to create the order — or override the required deposit below if payment was received."
                : `The required deposit amount is ${formatCurrency(requiredDeposit)}. You can override it below before verifying.`
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Card
            size="small"
            title={
              <Space>
                <FileImageOutlined />
                <Text>Proof of Payment (POP)</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
            data-testid="pop-upload-card"
          >
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Upload a bank POP (image or PDF). We will suggest reference and
              amount — you accept or reject before verifying.
            </Text>
            <Upload
              accept="image/*,application/pdf"
              multiple={false}
              showUploadList={false}
              beforeUpload={handlePopUpload}
              disabled={popUploading}
            >
              <Button
                icon={<UploadOutlined />}
                loading={popUploading}
                data-testid="pop-upload-button"
              >
                Upload POP
              </Button>
            </Upload>

            {popFiles.length > 0 && (
              <PopFilesRow>
                <Space wrap>
                  {popFiles.map((f, idx) => {
                    const url = f.downloadURL || f.downloadUrl || f.previewUrl;
                    const isImage = String(f.mime || "").startsWith("image/");
                    return (
                      <Space
                        key={f.id || idx}
                        direction="vertical"
                        align="center"
                        size={4}
                      >
                        {isImage && url ? (
                          <Image
                            src={url}
                            width={120}
                            alt={f.originalName || f.filename || "POP"}
                          />
                        ) : (
                          <Button
                            type="link"
                            icon={<FilePdfOutlined />}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {f.originalName || f.filename || `POP ${idx + 1}`}
                          </Button>
                        )}
                      </Space>
                    );
                  })}
                </Space>
              </PopFilesRow>
            )}

            {popSuggestions && (
              <PopSuggestionsBlock data-testid="pop-suggestions">
                {(popSuggestions.warnings || []).map((w, i) => (
                  <Alert
                    key={i}
                    type="warning"
                    showIcon
                    message={w}
                    style={{ marginBottom: 8 }}
                  />
                ))}
                <Text strong>Suggested from POP</Text>
                <SuggestionField>
                  <Space wrap>
                    <Text type="secondary">Amount:</Text>
                    <Tag color={popSuggestions.amount != null ? "blue" : "default"}>
                      {popSuggestions.amount != null
                        ? formatCurrency(popSuggestions.amount)
                        : "Not detected"}
                    </Tag>
                    {popSuggestions.amount != null && (
                      <>
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          onClick={() => acceptSuggestion("amount")}
                          data-testid="accept-amount-suggestion"
                          disabled={acceptedSuggestions.amount}
                        >
                          Use
                        </Button>
                        <Button
                          size="small"
                          icon={<CloseCircleOutlined />}
                          onClick={() => rejectSuggestion("amount")}
                          data-testid="reject-amount-suggestion"
                        >
                          Ignore
                        </Button>
                      </>
                    )}
                  </Space>
                </SuggestionField>
                <SuggestionField>
                  <Space wrap>
                    <Text type="secondary">Reference:</Text>
                    <Tag color={popSuggestions.reference ? "blue" : "default"}>
                      {popSuggestions.reference || "Not detected"}
                    </Tag>
                    {popSuggestions.reference && (
                      <>
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          onClick={() => acceptSuggestion("reference")}
                          data-testid="accept-reference-suggestion"
                          disabled={acceptedSuggestions.reference}
                        >
                          Use
                        </Button>
                        <Button
                          size="small"
                          icon={<CloseCircleOutlined />}
                          onClick={() => rejectSuggestion("reference")}
                          data-testid="reject-reference-suggestion"
                        >
                          Ignore
                        </Button>
                      </>
                    )}
                  </Space>
                </SuggestionField>
                {popSuggestions.provider && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Extractor: {popSuggestions.provider}
                  </Text>
                )}
              </PopSuggestionsBlock>
            )}
          </Card>

          <Form.Item
            label="Required Deposit (R)"
            required
            extra="Override here if the required amount should change before verification (including clearing a prior R0 waiver)."
          >
            <InputNumber
              prefix="R"
              style={{ width: "100%" }}
              min={0}
              max={
                Number.isFinite(totalCost) && totalCost > 0
                  ? totalCost
                  : undefined
              }
              precision={0}
              value={requiredDeposit}
              onChange={(value) => {
                const next = Number(value);
                if (!Number.isFinite(next)) return;
                applyRequiredDeposit(next, { touched: true, reset: false });
              }}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>

          {showOverrideReason && (
            <Form.Item
              label="Deposit Override Reason"
              required
              validateStatus={
                String(overrideReason || "").trim().length < 3
                  ? "error"
                  : undefined
              }
              help={
                String(overrideReason || "").trim().length < 3
                  ? "Reason is required (min 3 characters)"
                  : undefined
              }
            >
              <Input
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Why is the required deposit changing?"
              />
            </Form.Item>
          )}

          {showResetButton && (
            <div style={{ marginBottom: 16 }}>
              <Button type="link" onClick={handleResetToDefault} style={{ padding: 0 }}>
                Reset deposit to default ({formatCurrency(defaultDeposit)})
              </Button>
            </div>
          )}

          <Form.Item
            name="paymentReference"
            label="Payment Reference"
            rules={[
              {
                required: requiredDeposit > 0,
                message: "Payment reference is required",
              },
              {
                min: 3,
                message: "Payment reference must be at least 3 characters",
              },
            ]}
          >
            <Input
              placeholder="e.g., EFT123456, TxnRef789"
              prefix={<DollarOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="depositAmount"
            label="Deposit Amount Received"
            rules={[
              { required: true, message: "Deposit amount is required" },
              {
                type: "number",
                min: requiredDeposit,
                max:
                  Number.isFinite(totalCost) && totalCost > 0
                    ? totalCost
                    : Infinity,
                message: `Amount must be at least ${formatCurrency(requiredDeposit)} and not exceed the total amount`,
              },
            ]}
          >
            <InputNumber
              prefix="R"
              style={{ width: "100%" }}
              min={0}
              precision={2}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item
            name="verificationNotes"
            label="Verification Notes (Optional)"
          >
            <TextArea
              rows={3}
              placeholder="Any additional notes about the deposit verification..."
            />
          </Form.Item>

        </Form>
      ) : (
        <div>
          <div style={{ marginBottom: 24 }}>
            <Text>
              Deposit for quote <Text strong>{quote.id}</Text> has been
              verified.
              {verificationData?.order?.id && (
                <>
                  {" "}
                  Order <Text strong>{verificationData.order.id}</Text> has been
                  created.
                </>
              )}
            </Text>
          </div>

          {receiptPdfUrl && (
            <Card
              title={
                <Space>
                  <FilePdfOutlined />
                  <span>Deposit Receipt</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
              extra={
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadReceipt}
                >
                  Download Receipt
                </Button>
              }
            >
              <iframe
                src={receiptPdfUrl}
                style={{
                  width: "100%",
                  height: "500px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                }}
                title="Receipt Preview"
              />
            </Card>
          )}

          <Card
            title={
              <Space>
                <MessageOutlined />
                <span>Communication Templates</span>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Tabs defaultActiveKey="whatsapp">
              <TabPane
                tab={
                  <Space>
                    <MessageOutlined />
                    <span>WhatsApp</span>
                  </Space>
                }
                key="whatsapp"
              >
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Copy this template to send via WhatsApp
                  </Text>
                </div>
                <TextArea
                  value={whatsappTemplate}
                  readOnly
                  rows={10}
                  style={{ fontFamily: "monospace", fontSize: "13px" }}
                />
                <div style={{ marginTop: 12, textAlign: "right" }}>
                  <Button
                    type="primary"
                    icon={whatsappCopied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyWhatsApp}
                  >
                    {whatsappCopied ? "Copied!" : "Copy WhatsApp Template"}
                  </Button>
                </div>
              </TabPane>

              <TabPane
                tab={
                  <Space>
                    <MailOutlined />
                    <span>Email</span>
                  </Space>
                }
                key="email"
              >
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Copy this template to send via Email
                  </Text>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Subject: </Text>
                  <Text code>{emailTemplate.subject}</Text>
                </div>
                <TextArea
                  value={emailTemplate.body}
                  readOnly
                  rows={12}
                  style={{ fontFamily: "monospace", fontSize: "13px" }}
                />
                <div style={{ marginTop: 12, textAlign: "right" }}>
                  <Button
                    type="primary"
                    icon={emailCopied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyEmail}
                  >
                    {emailCopied ? "Copied!" : "Copy Email Template"}
                  </Button>
                </div>
              </TabPane>
            </Tabs>
          </Card>

        </div>
      )}
    </ResponsiveDrawer>
  );
};

export default DepositVerificationModal;
